// src/hooks/useRecruitment.js
// All Firestore hooks for the Recruitment ATS module

import { useState, useEffect } from "react";
import {
  collection, doc, query, where, orderBy, limit,
  onSnapshot, addDoc, updateDoc,
  serverTimestamp, getDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";

// ─── Utility: log an activity entry ───────────────────────────────────────
export async function logRecruitmentActivity({
  actorId, actorName, actorRole,
  action, entityType, entityId, entityName,
  oldValue = null, newValue = null,
  candidateId = null, jobId = null,
}) {
  try {
    await addDoc(collection(db, "recruitmentActivity"), {
      actorId, actorName, actorRole,
      action, entityType, entityId, entityName,
      oldValue, newValue,
      candidateId, jobId,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("[recruitmentActivity] log failed:", e);
  }
}

// ─── Job Openings ──────────────────────────────────────────────────────────
export function useJobOpenings(filters = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = query(collection(db, "jobOpenings"), orderBy("createdAt", "desc"));
    if (filters.status) q = query(collection(db, "jobOpenings"), where("status", "==", filters.status), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useJobOpenings]", e); setLoading(false); });
    return unsub;
  }, [filters.status]);

  return { data, loading };
}

export async function createJobOpening(data) {
  const ref = await addDoc(collection(db, "jobOpenings"), {
    ...data,
    applicantCount: 0,
    status: data.status || "Open",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function updateJobOpening(id, data) {
  await updateDoc(doc(db, "jobOpenings", id), { ...data, updatedAt: serverTimestamp() });
}

// ─── Candidates ────────────────────────────────────────────────────────────
export function useCandidates(jobId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = jobId
      ? query(collection(db, "candidates"), where("jobId", "==", jobId), orderBy("createdAt", "desc"))
      : query(collection(db, "candidates"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useCandidates]", e); setLoading(false); });
    return unsub;
  }, [jobId]);

  return { data, loading };
}

export async function createCandidate(data) {
  const ref = await addDoc(collection(db, "candidates"), {
    ...data,
    stage: data.stage || "Applied",
    aiScore: data.aiScore || 0,
    resumeScore: data.resumeScore || 0,
    tags: data.tags || [],
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    stageHistory: [{ stage: "Applied", movedAt: new Date().toISOString(), movedBy: data.createdByName || "System" }],
  });
  // Increment applicant count on job opening
  if (data.jobId) {
    try {
      const { increment } = await import("firebase/firestore");
      await updateDoc(doc(db, "jobOpenings", data.jobId), { applicantCount: increment(1) });
    } catch (_) {}
  }
  return ref.id;
}

export async function updateCandidateStage(candidateId, newStage, actorName, jobId) {
  const ref = doc(db, "candidates", candidateId);
  const snap = await getDoc(ref);
  const oldStage = snap.data()?.stage || "—";
  const stageHistory = snap.data()?.stageHistory || [];
  stageHistory.push({ stage: newStage, movedAt: new Date().toISOString(), movedBy: actorName });
  await updateDoc(ref, {
    stage: newStage,
    stageHistory,
    updatedAt: serverTimestamp(),
  });
  await logRecruitmentActivity({
    actorId: "", actorName, actorRole: "hr",
    action: "STAGE_MOVED", entityType: "candidate", entityId: candidateId,
    entityName: snap.data()?.name || candidateId,
    oldValue: oldStage, newValue: newStage, candidateId, jobId,
  });
}

export async function updateCandidate(id, data) {
  await updateDoc(doc(db, "candidates", id), { ...data, updatedAt: serverTimestamp() });
}

// ─── Interviews ────────────────────────────────────────────────────────────
export function useInterviews(candidateId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = candidateId
      ? query(collection(db, "interviews"), where("candidateId", "==", candidateId), orderBy("scheduledDate", "asc"))
      : query(collection(db, "interviews"), orderBy("scheduledDate", "asc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useInterviews]", e); setLoading(false); });
    return unsub;
  }, [candidateId]);

  return { data, loading };
}

export async function createInterview(data) {
  const ref = await addDoc(collection(db, "interviews"), {
    ...data,
    status: "Scheduled",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function submitInterviewFeedback(interviewId, feedback) {
  await updateDoc(doc(db, "interviews", interviewId), {
    feedback,
    status: "Completed",
    completedAt: serverTimestamp(),
  });
}

// ─── Recruitment Requests (from Team Leads) ────────────────────────────────
export function useRecruitmentRequests(status = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = status
      ? query(collection(db, "recruitmentRequests"), where("status", "==", status), orderBy("createdAt", "desc"))
      : query(collection(db, "recruitmentRequests"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useRecruitmentRequests]", e); setLoading(false); });
    return unsub;
  }, [status]);

  return { data, loading };
}

export function useMyRecruitmentRequests(teamLeadId) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!teamLeadId) return;
    const q = query(collection(db, "recruitmentRequests"), where("requestedById", "==", teamLeadId), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useMyRecruitmentRequests]", e); setLoading(false); });
    return unsub;
  }, [teamLeadId]);

  return { data, loading };
}

export async function createRecruitmentRequest(data) {
  const ref = await addDoc(collection(db, "recruitmentRequests"), {
    ...data,
    status: "Submitted",
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

export async function approveRecruitmentRequest(requestId, approvedById, approvedByName) {
  await updateDoc(doc(db, "recruitmentRequests", requestId), {
    status: "Approved",
    approvedById,
    approvedByName,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function rejectRecruitmentRequest(requestId, rejectedById, rejectedByName, reason) {
  await updateDoc(doc(db, "recruitmentRequests", requestId), {
    status: "Rejected",
    rejectedById,
    rejectedByName,
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });
}

// ─── Offers ────────────────────────────────────────────────────────────────
export function useOffers(candidateId = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = candidateId
      ? query(collection(db, "offers"), where("candidateId", "==", candidateId))
      : query(collection(db, "offers"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useOffers]", e); setLoading(false); });
    return unsub;
  }, [candidateId]);

  return { data, loading };
}

export async function createOffer(data) {
  const ref = await addDoc(collection(db, "offers"), {
    ...data,
    status: "Sent",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

// ─── Activity Feed ─────────────────────────────────────────────────────────
export function useRecruitmentActivity(limitCount = 50) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, "recruitmentActivity"), orderBy("createdAt", "desc"), limit(limitCount));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useRecruitmentActivity]", e); setLoading(false); });
    return unsub;
  }, [limitCount]);

  return { data, loading };
}

// ─── Referrals ─────────────────────────────────────────────────────────────
export function useReferrals(referredById = null) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let q = referredById
      ? query(collection(db, "referrals"), where("referredById", "==", referredById), orderBy("createdAt", "desc"))
      : query(collection(db, "referrals"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setData(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (e) => { console.error("[useReferrals]", e); setLoading(false); });
    return unsub;
  }, [referredById]);

  return { data, loading };
}

export async function createReferral(data) {
  const ref = await addDoc(collection(db, "referrals"), {
    ...data,
    status: "Submitted",
    createdAt: serverTimestamp(),
  });
  return ref.id;
}
